import { Container } from "@material-ui/core";
import Loading from "components/Loading";
import { RootState } from "config/store";
import { debounce, round } from "lodash";
import { useSnackbar } from "notistack";
import { useCallback, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useQuery } from "react-query";
import { useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { createSelling, getOrderById, modifySelling } from "shared/network/order.api";
import { ItemPriceHistory, ItemTaxHistory, Order } from "shared/types";
import { removeEmptyStrings } from "shared/util/removeEmptyStrings";
import ReceiptDialog from "./components/ReceiptDialog";
import SellingForm, { SellingFormValues } from "./components/SellingForm";

const Selling = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const location = useLocation();
  const history = useHistory();

  const query = new URLSearchParams(location.search);
  const orderId = query.get("id");

  const { tenant } = useSelector((state: RootState) => state.authentication.selectedRelTenant);

  const form = useForm<SellingFormValues>();
  const [relOrderItem, currency, cashRegister] = form.watch([
    "relOrderItem",
    "order.currency",
    "cashRegister",
  ]);

  const [order, setOrder] = useState<Order | null>(null);
  const [paymentType, setPaymentType] = useState<"CARD" | "CASH">("CASH");
  const [loading, setLoading] = useState(false);
  const [debouncedTotalPrice, setDebouncedTotalPrice] = useState(0);
  const [isBlockDialogOpen, setIsBlockDialogOpen] = useState(false);
  const [groupedItems, setGroupedItems] = useState<{ tax: any; items: any; total: any }[]>([]);

  const sellingOrderQuery = useQuery(
    ["sellingOrderQuery", orderId],
    async () => {
      if (orderId) {
        const { data } = await getOrderById(orderId, tenant?.id);
        return data?.item;
      }
      return Promise.reject();
    },
    {
      enabled: !!orderId,
      onSuccess: data => {
        if (!order) {
          setOrder(data);
          form.setValue("relOrderItem", data?.relOrderItems);
          form.setValue("order", data as any);
        }
      },
    },
  );

  function getTotal() {
    let relItems = form.watch("relOrderItem");
    let total = 0;
    relItems?.forEach(rel => {
      const priceHistory = rel.item.itemPriceHistories.find(
        (price: ItemPriceHistory) => price.isActive && currency.id === price?.currency?.id,
      );
      const price = priceHistory?.price ? Number(priceHistory?.price) : 0;
      const tax =
        (rel.item?.itemTaxHistories?.find((tax: ItemTaxHistory) => tax.isActive)?.tax?.percent ||
          0) / 100 || 0;
      const grossPrice = round(price * (1 + tax), 2);

      total += grossPrice * rel.quantity;
    });
    return total;
  }

  const totalPrice = getTotal();

  const onSubmit = async (values: SellingFormValues) => {
    setLoading(true);
    await saveOrder(values, true, false);
    setLoading(false);
  };

  async function saveOrder(
    values: SellingFormValues,
    needSnackbar: boolean,
    updateWindow: boolean,
  ) {
    try {
      if (!order) {
        const { data } = await createSelling(
          {
            ...removeEmptyStrings(values),
            order: { ...values.order, paymentType },
            relOrderItem: values.relOrderItem.map(item => {
              return {
                ...item,
                item: item.item,
                quantity: Number(item.quantity || 1),
              };
            }),
          },
          tenant.id,
        );
        if (data?.item?.id) {
          history.replace(`/selling?id=${data?.item?.id}`);
        }
      } else {
        await modifySelling(
          {
            ...removeEmptyStrings(values),
            order: { ...order, ...values.order, paymentType },
            relOrderItem: values.relOrderItem.map(item => {
              return {
                ...item,
                item: item.item,
                quantity: Number(item.quantity || 1),
              };
            }),
          },
          tenant.id,
        );
      }
      sellingOrderQuery?.refetch();
      if (needSnackbar) {
        enqueueSnackbar(t("common:notification.save.success"), { variant: "success" });
      }
    } catch (e: any) {
      if (needSnackbar) {
        enqueueSnackbar(t("common:notification.save.failure"), { variant: "error" });
      }
    }
  }

  const updateDebouncedTotalPrice = useCallback(
    debounce((newTotalPrice: number) => {
      setDebouncedTotalPrice(newTotalPrice);
    }, 500),
    [],
  );

  useEffect(() => {
    updateDebouncedTotalPrice(totalPrice);
  }, [totalPrice, updateDebouncedTotalPrice]);

  useEffect(() => {
    if (relOrderItem) {
      form.handleSubmit(values => saveOrder(values, false, false))();
    }
  }, [debouncedTotalPrice]);

  return (
    <Container maxWidth="xl">
      <Loading open={loading} />
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <FormProvider {...form}>
          <SellingForm
            paymentType={paymentType}
            setPaymentType={setPaymentType}
            totalPrice={totalPrice}
            order={order}
            saveOrder={saveOrder}
            setGroupedItems={setGroupedItems}
            setIsBlockDialogOpen={setIsBlockDialogOpen}
          />
        </FormProvider>
      </form>
      <ReceiptDialog
        groupedItems={groupedItems}
        isBlockDialogOpen={isBlockDialogOpen}
        setIsBlockDialogOpen={setIsBlockDialogOpen}
        currency={currency}
        cashRegister={cashRegister}
      />
    </Container>
  );
};

export default Selling;
